import { expandUnicodeFractions, parseQuantity, QUANTITY_TOKEN } from '../quantity/fraction.js';
import { normalizeUnit, UNIT_ALIASES_BY_LENGTH } from '../units/normalize.js';
import { getUnit } from '../units/catalog.js';
const UNQUANTIFIED_PATTERNS = [
    /\b(to taste|as needed|as required|for serving|to serve|for garnish|for frying|for greasing|for dusting)\b/i,
    /\b(nach geschmack|nach belieben|nach bedarf|zum servieren|zum garnieren|zum braten|zum bestreichen|zum bestäuben|zum ausrollen)\b/i,
];
const LEADING_VAGUE_QUANTITY = /^(some|a little|a bit of|a few|etwas|ein wenig|einige|ein paar|wenig)\s+/i;
const RANGE_SEPARATOR = String.raw `(?:\s*(?:-|–|—|to|bis|or|oder)\s*)`;
const LEADING_QUANTITY = new RegExp(`^(${QUANTITY_TOKEN})(?:${RANGE_SEPARATOR}(${QUANTITY_TOKEN}))?\\s*`, 'i');
const OPTIONAL_MARKERS = /\(?\b(optional|nach wunsch|wenn gewünscht|if desired)\b\)?/i;
const ARTICLE_AFTER_UNIT = /^(of|von|an)\s+/i;
const NOTE_SPLIT = /\s*[,;]\s+|\s+-\s+|\s+–\s+/;
function extractUnit(rest) {
    const lower = rest.toLowerCase();
    for (const alias of UNIT_ALIASES_BY_LENGTH) {
        if (alias.length === 0)
            continue;
        if (!lower.startsWith(alias))
            continue;
        const after = rest.slice(alias.length);
        // The alias must be followed by end, whitespace or punctuation, not by more letters.
        if (after.length > 0 && /^[a-zäöüß]/i.test(after) && !/^[.]/.test(after))
            continue;
        const unit = normalizeUnit(alias);
        if (!unit)
            continue;
        // Guard: single letters like "c" or "x" only count when followed by whitespace and a word.
        if (alias.length === 1 && !/^\s+\S/.test(after))
            continue;
        let remainder = after.replace(/^[.\s]+/, '');
        remainder = remainder.replace(ARTICLE_AFTER_UNIT, '');
        return { unitCode: unit.code, unitText: null, remainder };
    }
    return { unitCode: null, unitText: null, remainder: rest };
}
/**
 * Parse a free-text ingredient line such as "2-3 tbsp olive oil, extra virgin" or
 * "500 g Mehl, gesiebt" into quantity, unit, name and note. Never throws; anything it
 * cannot interpret ends up in `name` (and `quantityText` when a vague amount is found).
 */
export function parseIngredientLine(raw) {
    const originalText = raw;
    let text = expandUnicodeFractions(raw).replace(/\s+/g, ' ').trim();
    let optional = false;
    if (OPTIONAL_MARKERS.test(text)) {
        optional = true;
        text = text
            .replace(OPTIONAL_MARKERS, '')
            .replace(/\(\s*\)/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        text = text.replace(/\s*,\s*$/, '');
    }
    let quantity = null;
    let quantityMax = null;
    let quantityText = null;
    let scalable = true;
    const vague = text.match(LEADING_VAGUE_QUANTITY);
    if (vague) {
        quantityText = vague[1].toLowerCase();
        text = text.slice(vague[0].length);
        scalable = false;
    }
    else {
        const match = text.match(LEADING_QUANTITY);
        if (match) {
            const q = parseQuantity(match[1]);
            if (q !== null) {
                quantity = q;
                if (match[2]) {
                    const qm = parseQuantity(match[2]);
                    if (qm !== null && Number(qm) >= Number(q))
                        quantityMax = qm;
                }
                text = text.slice(match[0].length);
            }
        }
    }
    let unitCode = null;
    let unitText = null;
    if (quantity !== null || quantityText !== null) {
        const extracted = extractUnit(text);
        unitCode = extracted.unitCode;
        unitText = extracted.unitText;
        text = extracted.remainder;
        const unit = getUnit(unitCode);
        if (unit && !unit.scalableByDefault)
            scalable = false;
    }
    // "1 can (400 g) tomatoes" -> note "400 g"
    let note = null;
    const paren = text.match(/\(([^)]*)\)/);
    if (paren) {
        note = paren[1].trim() || null;
        text = text.replace(paren[0], ' ').replace(/\s+/g, ' ').trim();
    }
    for (const pattern of UNQUANTIFIED_PATTERNS) {
        const m = text.match(pattern);
        if (m) {
            quantityText = quantityText ? `${quantityText}, ${m[1].toLowerCase()}` : m[1].toLowerCase();
            text = text
                .replace(pattern, '')
                .replace(/\s*,\s*$/, '')
                .replace(/\s+/g, ' ')
                .trim();
            if (quantity === null)
                scalable = false;
        }
    }
    const parts = text
        .split(NOTE_SPLIT)
        .map((p) => p.trim())
        .filter(Boolean);
    let name = parts.shift() ?? '';
    if (parts.length) {
        note = note ? `${parts.join(', ')} (${note})` : parts.join(', ');
    }
    name = name.replace(/^[,.\s-]+|[,.\s-]+$/g, '');
    if (!name) {
        name = originalText.trim();
    }
    return {
        quantity,
        quantityMax,
        quantityText,
        unitCode,
        unitText,
        name,
        note,
        optional,
        scalable,
        originalText,
    };
}
export function parseIngredientLines(lines) {
    return lines
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map(parseIngredientLine);
}
//# sourceMappingURL=parse-line.js.map